import React, { useState, useEffect, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { Level } from '@tiptap/extension-heading';
import './Toolbar.css';

interface ToolbarProps {
  editor: Editor | null;
  onAddImage?: (file: File) => void;
  onAddFile?: (file: File) => void;
  onSetLink?: () => void;
  uploadProgress?: { [key: string]: number };
}

const COLORS = [
  '#000000', // Black
  '#FFFFFF', // White
  '#FF0000', // Red
  '#00FF00', // Green
  '#0000FF', // Blue
  '#FFFF00', // Yellow
  '#FF00FF', // Magenta
  '#00FFFF', // Cyan
  '#FFA500', // Orange
  '#800080', // Purple
  '#008000', // Dark Green
  '#000080', // Navy
];

const FONT_FAMILIES = [
  'Segoe UI',
  'Arial',
  'Times New Roman',
  'Courier New',
  'Georgia',
];

const FONT_SIZES = [
  '8pt',
  '10pt',
  '12pt',
  '14pt',
  '16pt',
  '18pt',
  '20pt',
  '24pt',
  '36pt',
];

const HEADINGS: Array<{ tag: string; label: string }> = [
  { tag: 'paragraph', label: 'Paragraph' },
  { tag: 'h1', label: 'Heading 1' },
  { tag: 'h2', label: 'Heading 2' },
  { tag: 'h3', label: 'Heading 3' },
  { tag: 'h4', label: 'Heading 4' },
  { tag: 'preformatted', label: 'Preformatted' },
];

export function Toolbar({ editor, onAddImage, onAddFile, onSetLink, uploadProgress }: ToolbarProps) {
  const [showTextColors, setShowTextColors] = useState(false);
  const [showBgColors, setShowBgColors] = useState(false);
  const [showFontFamily, setShowFontFamily] = useState(false);
  const [showFontSize, setShowFontSize] = useState(false);
  const [showHeadings, setShowHeadings] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [showTableOptions, setShowTableOptions] = useState(false);
  const [currentFontFamily, setCurrentFontFamily] = useState('Segoe UI');
  const [currentFontSize, setCurrentFontSize] = useState('12pt');
  const [currentTextColor, setCurrentTextColor] = useState('#000000');
  const [currentBgColor, setCurrentBgColor] = useState('transparent');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Refs for dropdowns
  const imageInputRef = useRef<HTMLInputElement>(null);
  const moreOptionsRef = useRef<HTMLDivElement>(null);
  const fontFamilyRef = useRef<HTMLButtonElement>(null);
  const fontSizeRef = useRef<HTMLButtonElement>(null);
  const textColorRef = useRef<HTMLButtonElement>(null);
  const bgColorRef = useRef<HTMLButtonElement>(null);
  const headingsRef = useRef<HTMLButtonElement>(null);
  const tableRef = useRef<HTMLButtonElement>(null);

  const positionDropdown = (buttonRef: React.RefObject<HTMLElement>, setShow: (show: boolean) => void) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  };

  useEffect(() => {
    // Handle clicks outside dropdowns
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (moreOptionsRef.current && !moreOptionsRef.current.contains(target)) {
        closeAllDropdowns();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Update font family when selection changes
    const marks = editor?.getAttributes('textStyle');
    if (marks?.fontFamily) {
      setCurrentFontFamily(marks.fontFamily);
    }
    if (marks?.color) {
      setCurrentTextColor(marks.color);
    }

    // Update font size when selection changes
    const fontSizeMark = editor?.getAttributes('fontSize');
    if (fontSizeMark?.size) {
      setCurrentFontSize(fontSizeMark.size);
    }

    // Update background color when selection changes
    const highlightMark = editor?.getAttributes('highlight');
    if (highlightMark?.color) {
      setCurrentBgColor(highlightMark.color);
    }
  }, [editor?.state.selection, editor]);

  // Determine if any upload is in progress
  const isUploading = uploadProgress && Object.values(uploadProgress).some(p => p < 100);

  if (!editor) {
    return null;
  }

  const closeAllDropdowns = () => {
    closeAllSubMenus();
    setShowMoreOptions(false);
  };

  const closeAllSubMenus = () => {
    setShowTextColors(false);
    setShowBgColors(false);
    setShowFontFamily(false);
    setShowFontSize(false);
    setShowHeadings(false);
    //setShowTableOptions(false);
  };

  const toggleTextColors = () => {
    setShowTextColors(!showTextColors);
    setShowBgColors(false);
    setShowFontFamily(false);
    setShowFontSize(false);
    setShowHeadings(false);
    setShowTableOptions(false);
  };

  const toggleBgColors = () => {
    setShowBgColors(!showBgColors);
    setShowTextColors(false);
    setShowFontFamily(false);
    setShowFontSize(false);
    setShowHeadings(false);
    setShowTableOptions(false);
  };

  const toggleFontFamily = () => {
    setShowFontFamily(!showFontFamily);
    setShowTextColors(false);
    setShowBgColors(false);
    setShowFontSize(false);
    setShowHeadings(false);
    setShowTableOptions(false);
  };

  const toggleFontSize = () => {
    setShowFontSize(!showFontSize);
    setShowTextColors(false);
    setShowBgColors(false);
    setShowFontFamily(false);
    setShowHeadings(false);
    setShowTableOptions(false);
  };

  const toggleHeadings = () => {
    setShowHeadings(!showHeadings);
    setShowTextColors(false);
    setShowBgColors(false);
    setShowFontFamily(false);
    setShowFontSize(false);
    setShowTableOptions(false);
  };

  const toggleTableOptions = () => {
    setShowTableOptions(!showTableOptions);
    setShowTextColors(false);
    setShowBgColors(false);
    setShowFontFamily(false);
    setShowFontSize(false);
    setShowHeadings(false);
  };

  const getCurrentHeading = () => {
    if (editor?.isActive('heading', { level: 1 })) return 'Heading 1';
    if (editor?.isActive('heading', { level: 2 })) return 'Heading 2';
    if (editor?.isActive('heading', { level: 3 })) return 'Heading 3';
    if (editor?.isActive('heading', { level: 4 })) return 'Heading 4';
    if (editor?.isActive('codeBlock')) return 'Preformatted';
    return 'Paragraph';
  };

  const setHeading = (tag: string) => {
    if (tag === 'paragraph') {
      editor?.chain().focus().setParagraph().run();
    } else if (tag === 'preformatted') {
      editor?.chain().focus().toggleCodeBlock().run();
    } else {
      const level = parseInt(tag.substring(1), 10) as Level;
      editor?.chain().focus().toggleHeading({ level }).run();
    }
    setShowHeadings(false);
  };

  const insertTable = () => {
    console.log('Inserting table' + editor);
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    setShowTableOptions(false);
  };

  const addColumnBefore = () => {
    editor?.chain().focus().addColumnBefore().run();
  };

  const addColumnAfter = () => {
    editor?.chain().focus().addColumnAfter().run();
  };

  const deleteColumn = () => {
    editor?.chain().focus().deleteColumn().run();
  };

  const addRowBefore = () => {
    editor?.chain().focus().addRowBefore().run();
  };

  const addRowAfter = () => {
    editor?.chain().focus().addRowAfter().run();
  };

  const deleteRow = () => {
    editor?.chain().focus().deleteRow().run();
  };

  const deleteTable = () => {
    editor?.chain().focus().deleteTable().run();
  };

  const mergeCells = () => {
    editor?.chain().focus().mergeCells().run();
  };

  const splitCell = () => {
    editor?.chain().focus().splitCell().run();
  };

  const toggleHeaderCell = () => {
    editor?.chain().focus().toggleHeaderCell().run();
  };

  const toggleHeaderRow = () => {
    editor?.chain().focus().toggleHeaderRow().run();
  };

  const toggleHeaderColumn = () => {
    editor?.chain().focus().toggleHeaderColumn().run();
  };

  return (
    <div className="toolbar" onClick={() => {
      setShowHeadings(false);
      setShowMoreOptions(false);
      setShowFontFamily(false);
      setShowFontSize(false);
      setShowTextColors(false);
      setShowBgColors(false);
      setShowTableOptions(false);
    }}>
      {/* Font size selector */}
      <div className="toolbar-group">
        <select 
          value={currentFontSize} 
          onChange={(e) => {
            editor?.chain().focus().setMark('fontSize', { size: e.target.value }).run();
            setCurrentFontSize(e.target.value);
          }}
          className="toolbar-select"
        >
          {FONT_SIZES.map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </div>

      {/* Basic formatting group */}
      <div className="toolbar-group">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Bold"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M8 11h4.5a2.5 2.5 0 1 0 0-5H8v5zm10 4.5a2.5 2.5 0 0 1-2.5 2.5H8V13h7.5a2.5 2.5 0 0 1 2.5 2.5z"/>
          </svg>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italic"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M15 20H7v-2h2.927l2.116-12H9V4h8v2h-2.927l-2.116 12H15z"/>
          </svg>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'is-active' : ''}
          title="Underline"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M8 3v9a4 4 0 1 0 8 0V3h2v9a6 6 0 1 1-12 0V3h2zM4 20h16v2H4v-2z"/>
          </svg>
        </button>
      </div>

      {/* Alignment group */}
      <div className="toolbar-group">
        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
          title="Align Left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M3 4h18v2H3V4zm0 15h18v2H3v-2zm0-5h12v2H3v-2zm0-5h18v2H3V9z"/>
          </svg>
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
          title="Align Center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M3 4h18v2H3V4zm4 15h10v2H7v-2zm-4-5h18v2H3v-2zm4-5h10v2H7V9z"/>
          </svg>
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
          title="Align Right"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M3 4h18v2H3V4zm4 15h14v2H7v-2zm-4-5h18v2H3v-2zm4-5h14v2H7V9z"/>
          </svg>
        </button>
      </div>

      {/* Lists group */}
      <div className="toolbar-group">
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet List"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M8 4h13v2H8V4zM4.5 6.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm0 7a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm0 6.9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM8 11h13v2H8v-2zm0 7h13v2H8v-2z"/>
          </svg>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Numbered List"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M8 4h13v2H8V4zM5 3v3h1v1H3V6h1V4H3V3h2zM3 14v-2.5h2V11H3v-1h3v2.5H4v.5h2v1H3zm2 5.5H3v-1h2V18H3v-1h3v4H3v-1h2v-.5zM8 11h13v2H8v-2zm0 7h13v2H8v-2z"/>
          </svg>
        </button>
      </div>

      {/* Table controls */}
      <div className="toolbar-group">
        <div className="toolbar-group" style={{ position: 'relative' }}>
          <button
            ref={tableRef}
            onClick={(e) => {
              e.stopPropagation();
              toggleTableOptions();
            }}
            className="toolbar-button"
            title="Table"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path fill="none" stroke="currentColor" strokeWidth="2" d="M3 3h18v18H3V3zm0 9h18M3 15h18M12 3v18"/>
            </svg>
          </button>
          {showTableOptions && (
            <div className="dropdown-menu table-menu" style={{ minWidth: '200px' }}>
              {!editor?.isActive('table') ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    insertTable();
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                >
                  Insert Table
                </button>
              ) : (
                <>
                  <div className="px-3 py-2 text-xs font-medium text-gray-500">Table Controls</div>
                  <button onClick={addColumnBefore} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Add Column Before
                  </button>
                  <button onClick={addColumnAfter} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Add Column After
                  </button>
                  <button onClick={deleteColumn} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Delete Column
                  </button>
                  <button onClick={addRowBefore} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Add Row Before
                  </button>
                  <button onClick={addRowAfter} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Add Row After
                  </button>
                  <button onClick={deleteRow} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Delete Row
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button onClick={mergeCells} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Merge Cells
                  </button>
                  <button onClick={splitCell} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Split Cell
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button onClick={toggleHeaderCell} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Toggle Header Cell
                  </button>
                  <button onClick={toggleHeaderRow} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Toggle Header Row
                  </button>
                  <button onClick={toggleHeaderColumn} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100">
                    Toggle Header Column
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button onClick={deleteTable} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50">
                    Delete Table
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* More options button */}
      <div className="toolbar-more-options" ref={moreOptionsRef}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMoreOptions(!showMoreOptions);
          }}
          className="toolbar-button"
          title="More options"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M12 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 14c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-7c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
          </svg>
        </button>

        {/* More options dropdown */}
        {showMoreOptions && (
          <div 
            className="more-options-dropdown" 
            onClick={(e) => {e.stopPropagation(); closeAllSubMenus()}}
          >
            {/* Font options */}
            <div className="toolbar-group">
              <div className="toolbar-group" style={{ position: 'relative' }}>
                <button
                  ref={headingsRef}
                  onClick={(e) => {
                    toggleHeadings();
                    e.stopPropagation();
                    positionDropdown(headingsRef, setShowHeadings);
                  }}
                  className="toolbar-button heading-button"
                  title="Paragraph format"
                  type="button"
                >
                  {getCurrentHeading()}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" className="ml-1">
                    <path fill="currentColor" d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
                {showHeadings && (
                  <div className="dropdown-menu">
                    {HEADINGS.map(({ tag, label }) => {
                      const isActive = 
                        (tag === 'paragraph' && editor?.isActive('paragraph')) ||
                        (tag === 'preformatted' && editor?.isActive('codeBlock')) ||
                        (tag.startsWith('h') && editor?.isActive('heading', { level: parseInt(tag.substring(1), 10) }));
                      
                      return (
                        <button
                          key={tag}
                          onClick={(e) => {
                            e.stopPropagation();
                            setHeading(tag);
                            setShowHeadings(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                            isActive ? 'bg-gray-100 font-medium' : ''
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="toolbar-group" style={{ position: 'relative' }}>
                <button
                  ref={fontFamilyRef}
                  onClick={(e) => {
                    toggleFontFamily();
                    e.stopPropagation();
                    positionDropdown(fontFamilyRef, setShowFontFamily);
                  }}
                  className="toolbar-button font-family-button"
                  title="Font Family"
                >
                  {currentFontFamily}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" className="ml-1">
                    <path fill="currentColor" d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
                {showFontFamily && (
                  <div className="dropdown-menu">
                    {FONT_FAMILIES.map(font => (
                      <button
                        key={font}
                        onClick={(e) => {
                          e.stopPropagation();
                          editor?.chain().focus().setFontFamily(font).run();
                          setCurrentFontFamily(font);
                          setShowFontFamily(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                          editor?.isActive({ fontFamily: font }) ? 'bg-gray-100 font-medium' : ''
                        }`}
                        style={{ fontFamily: font }}
                      >
                        {font}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="toolbar-group" style={{ position: 'relative' }}>
                <button
                  ref={fontSizeRef}
                  onClick={(e) => {
                    toggleFontSize();
                    e.stopPropagation();
                    positionDropdown(fontSizeRef, setShowFontSize);
                  }}
                  className="toolbar-button font-size-button"
                  title="Font Size"
                >
                  {currentFontSize}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" className="ml-1">
                    <path fill="currentColor" d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
                {showFontSize && (
                  <div className="dropdown-menu">
                    {FONT_SIZES.map(size => {
                      const isActive = editor?.isActive('fontSize', { size });
                      return (
                        <button
                          key={size}
                          onClick={(e) => {
                            e.stopPropagation();
                            editor?.chain().focus().setMark('fontSize', { size }).run();
                            setCurrentFontSize(size);
                            setShowFontSize(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                            isActive ? 'bg-gray-100 font-medium' : ''
                          }`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Color options */}
            <div className="toolbar-group">
              <div className="color-picker-container">
                <button
                  ref={textColorRef}
                  onClick={(e) => {
                    toggleTextColors();
                    e.stopPropagation();
                    positionDropdown(textColorRef, setShowTextColors);
                  }}
                  className="toolbar-button color-button"
                  title="Text Color"
                  style={{ '--current-color': currentTextColor } as React.CSSProperties}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M15.246 14H8.754l-1.6 4H5l7-18h2l7 18h-2.154l-1.6-4zm-.8-2L12 5.885 9.554 12h4.892z"/>
                    <rect x="4" y="19" width="16" height="3" fill={currentTextColor}/>
                  </svg>
                </button>
                {showTextColors && (
                  <div className="color-dropdown" style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`
                  }}>
                    {COLORS.map(color => (
                      <button
                        key={color}
                        className={`color-swatch ${color === currentTextColor ? 'is-active' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => {
                          editor.chain().focus().setMark('textStyle', { color: color }).run();
                          setCurrentTextColor(color);
                          setShowTextColors(false);
                        }}
                        title={`Text color: ${color}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="color-picker-container">
                <button
                  ref={bgColorRef}
                  onClick={(e) => {
                    toggleBgColors();
                    e.stopPropagation();
                    positionDropdown(bgColorRef, setShowBgColors);
                  }}
                  className="toolbar-button color-button"
                  title="Background Color"
                  style={{ '--current-color': currentBgColor } as React.CSSProperties}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M15.246 14H8.754l-1.6 4H5l7-18h2l7 18h-2.154l-1.6-4zm-.8-2L12 5.885 9.554 12h4.892z"/>
                    <rect x="4" y="19" width="16" height="3" fill={currentBgColor === 'transparent' ? '#000000' : currentBgColor}/>
                  </svg>
                </button>
                {showBgColors && (
                  <div className="color-dropdown" style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`
                  }}>
                    <button
                      className={`color-swatch ${currentBgColor === 'transparent' ? 'is-active' : ''}`}
                      style={{ 
                        backgroundColor: 'white',
                        backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                        backgroundSize: '10px 10px',
                        backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px'
                      }}
                      onClick={() => {
                        editor.chain().focus().unsetMark('highlight').run();
                        setCurrentBgColor('transparent');
                        setShowBgColors(false);
                      }}
                      title="No background color"
                    />
                    {COLORS.map(color => (
                      <button
                        key={color}
                        className={`color-swatch ${color === currentBgColor ? 'is-active' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => {
                          editor.chain().focus().setMark('highlight', { color }).run();
                          setCurrentBgColor(color);
                          setShowBgColors(false);
                        }}
                        title={`Background color: ${color}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Alignment options */}
            <div className="toolbar-group">
              <button
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
                title="Align Left"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M3 4h18v2H3V4zm0 15h18v2H3v-2zm0-5h12v2H3v-2zm0-5h18v2H3V9z"/>
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
                title="Align Center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M3 4h18v2H3V4zm4 15h10v2H7v-2zm-4-5h18v2H3v-2zm4-5h10v2H7V9z"/>
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
                title="Align Right"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M3 4h18v2H3V4zm4 15h14v2H7v-2zm-4-5h18v2H3v-2zm4-5h14v2H7V9z"/>
                </svg>
              </button>
            </div>

            {/* List options */}
            <div className="toolbar-group">
              <button
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={editor.isActive('bulletList') ? 'is-active' : ''}
                title="Bullet List"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 4h13v2H8V4zM4.5 6.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm0 7a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm0 6.9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM8 11h13v2H8v-2zm0 7h13v2H8v-2z"/>
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={editor.isActive('orderedList') ? 'is-active' : ''}
                title="Numbered List"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 4h13v2H8V4zM5 3v3h1v1H3V6h1V4H3V3h2zM3 14v-2.5h2V11H3v-1h3v2.5H4v.5h2v1H3zm2 5.5H3v-1h2V18H3v-1h3v4H3v-1h2v-.5zM8 11h13v2H8v-2zm0 7h13v2H8v-2z"/>
                </svg>
              </button>
            </div>

            {/* Code and quote options */}
            <div className="toolbar-group">
              <button
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                className={editor.isActive('blockquote') ? 'is-active' : ''}
                title="Citation"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4.583 17c.23.516.346 1.09.346 1.72 0 1.342-.524 2.392-1.571 3.147C14.88 19.622 13.433 20 11.586 20c-1.64 0-3.263-.381-4.87-1.144V16.6c1.52.877 3.075 1.316 4.666 1.316 2.551 0 3.83-.732 3.839-2.197a2.21 2.21 0 0 0-.648-1.603l-.12-.117H3v-2h18v2h-3.846zm-4.078-3H7.629a4.086 4.086 0 0 1-.481-.522C6.716 9.92 6.5 9.246 6.5 8.452c0-1.236.466-2.287 1.397-3.153C8.83 4.433 10.271 4 12.222 4c1.471 0 2.879.328 4.222.984v2.152c-1.2-.687-2.515-1.03-3.946-1.03-2.48 0-3.719.782-3.719 2.346 0 .42.218.786.654 1.099.436.313.974.562 1.613.75.62.18 1.297.414 2.03.699z"/>
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().toggleCode().run()}
                className={editor.isActive('code') ? 'is-active' : ''}
                title="Inline Code"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.7 15.9L4.8 12l3.9-3.9c.39-.39.39-1.01 0-1.4a.9839.9839 0 0 0-1.4 0l-4.59 4.59c-.39.39-.39 1.02 0 1.41l4.59 4.6c.39.39 1.01.39 1.4 0 .39-.39.39-1.01 0-1.4zm6.6 0l3.9-3.9-3.9-3.9a.9839.9839 0 0 1 0-1.4c.39-.39 1.01-.39 1.4 0l4.59 4.59c.39.39.39 1.02 0 1.41l-4.59 4.6c-.39.39-1.01.39-1.4 0a.9839.9839 0 0 1 0-1.4z"/>
                </svg>
              </button>
              <button
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                className={editor.isActive('codeBlock') ? 'is-active' : ''}
                title="Code Block"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 2v14h16V5H4zm16 7l-3.536 3.536-1.414-1.415L17.172 12 15.05 9.879l1.414-1.415L20 12zM6.828 12l2.122 2.121-1.414 1.415L4 12l3.536-3.536L8.95 9.88 6.828 12zm4.416 5H9.116l3.64-10h2.128l-3.64 10z"/>
                </svg>
              </button>
            </div>

            {/* Link and media options */}
            <div className="toolbar-group">
              <button
                onClick={onSetLink}
                className={`toolbar-button ${editor?.isActive('link') ? 'active' : ''}`}
                title="Add link"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </button>
              <label className="toolbar-button" title="Insert Image" style={{ position: 'relative' }}>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      console.log('[Toolbar] Image file selected:', {
                        name: file.name,
                        size: file.size,
                        type: file.type
                      });
                    } else {
                      console.log('[Toolbar] No file selected');
                    }
                    if (file && onAddImage) {
                      onAddImage(file);
                    }
                    // Reset input value so selecting the same file again triggers onChange
                    if (imageInputRef.current) imageInputRef.current.value = '';
                  }}
                  ref={imageInputRef}
                  style={{ display: 'none' }}
                />
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M4.828 21l-.02.02-.021-.02H2.992A.993.993 0 0 1 2 20.007V3.993A1 1 0 0 1 2.992 3h18.016c.548 0 .992.445.992.993v16.014c0 .548-.444.993-.992.993H4.828zM20 15V5H4v14L14 9l6 6zm0 2.828l-6-6L6.828 19H20v-1.172zM8 11a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
                </svg>
                {isUploading && (
                  <span className="absolute inset-0 flex items-center justify-center bg-white/70 rounded">
                    <svg className="animate-spin h-5 w-5 text-teal-500" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  </span>
                )}
              </label>
              <label className="toolbar-button" title="Insert File" style={{ position: 'relative' }}>
                <input
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && onAddFile) {
                      onAddFile(file);
                    }
                  }}
                  className="hidden"
                />
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M9 2.003V2h10.998C20.55 2 21 2.455 21 2.992v18.016a.993.993 0 0 1-.993.992H3.993A1 1 0 0 1 3 20.993V8l6-5.997zM5.83 8H9V4.83L5.83 8zM11 4v5a1 1 0 0 1-1 1H5v10h14V4h-8z"/>
                </svg>
                {isUploading && (
                  <span className="absolute inset-0 flex items-center justify-center bg-white/70 rounded">
                    <svg className="animate-spin h-5 w-5 text-teal-500" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  </span>
                )}
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}